import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Link, Typography, withStyles } from '@material-ui/core'
import { Icon } from '@iconify/react'
import MuiAccordion from '@material-ui/core/Accordion'
import MuiAccordionSummary from '@material-ui/core/AccordionSummary'
import MuiAccordionDetails from '@material-ui/core/AccordionDetails'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import PaidAccess from 'src/components/user-access/components/payment-wall/components/paid-access'
import { getCertificatePaymentSettingAction } from 'src/reducers/certificate/action'
import styles from './styles'

const Accordion = withStyles({
  root: {
    width: '100%',
    boxShadow: 'none',
    borderRadius: '8px',
    backgroundColor: '#F4F6F8',
    '&:not(:last-child)': { borderBottom: 0 },
    '&:before': { display: 'none' },
    '&$expanded': {
      margin: 'auto',
      boxShadow: 'none',
      backgroundColor: '#F4F6F8',
    },
  },
  expanded: { backgroundColor: '#F4F6F8' },
})(MuiAccordion)

const AccordionSummary = withStyles({
  root: {
    backgroundColor: '#F4F6F8',
    minHeight: 56,
    borderRadius: '8px',
    gap: 8,
    '&$expanded': {
      minHeight: 56,
      gap: 8,
      backgroundColor: '#F4F6F8',
    },
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
      backgroundColor: '#F4F6F8',
    },
  },
})(MuiAccordionSummary)

const AccordionDetails = withStyles(theme => ({
  root: {
    backgroundColor: '#F4F6F8',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    borderRadius: '8px',
  },
}))(MuiAccordionDetails)

const PaymentSetup = ({ classes }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const collection = useSelector(state => state.collection.collection)
  const collectionSettings = useSelector(
    state => state.collection.collectionSettings,
  )
  const certificate = useSelector(state => state.certificate.certificate)
  const paymentSetting = useSelector(
    state => state.certificate?.paymentSetting,
  )
  const landingPageSettings = useSelector(
    state => state.collection.landingPageSettings,
  )

  const isPublicAccess = collectionSettings?.accessRule === 0
  const headerSection = landingPageSettings?.sections?.find(
    s => s.type === 'Header',
  )
  const headerContentType = headerSection?.content?.type
  const isPaidCompatibleHeader =
    headerContentType === 2 || headerContentType === 3

  const [expanded, setExpanded] = useState(false)
  const [loaded, setLoaded] = useState(false)
  // Once the user toggles the accordion themselves we stop auto-managing it,
  // otherwise a redux update could yank it open while they're closing it.
  const userTouched = React.useRef(false)

  useEffect(() => {
    if (collection?.id) {
      // Track GET completion locally — Redux can't distinguish "haven't fetched"
      // from "fetched and empty", and PaidAccess's mount-time defaulting needs
      // that signal to avoid destructive auto-writes on a fresh row.
      dispatch(getCertificatePaymentSettingAction(collection.id, () => {
        setLoaded(true)
      }))
    }
  }, [collection?.id])

  // Auto-expand on initial load when there's saved payment data so the user
  // doesn't have to click in just to confirm what's already configured.
  useEffect(() => {
    if (userTouched.current || !loaded || isPublicAccess) return
    const hasSavedData =
      paymentSetting?.isPaymentConfigured ||
      !!paymentSetting?.paymentLink ||
      (paymentSetting?.amount != null && paymentSetting.amount > 0)
    if (hasSavedData) {
      setExpanded(true)
    }
  }, [
    loaded,
    paymentSetting?.isPaymentConfigured,
    paymentSetting?.paymentLink,
    paymentSetting?.amount,
    isPublicAccess,
  ])

  const handleToggle = () => {
    userTouched.current = true
    setExpanded(prev => !prev)
  }

  if (!certificate?.isActive) {
    return null
  }

  return (
    <Accordion
      expanded={isPublicAccess ? false : expanded}
      disabled={isPublicAccess}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        onClick={isPublicAccess ? undefined : handleToggle}
      >
        <div className={classes.headingWrapper}>
          <Typography className={classes.heading}>Set up Payments</Typography>
          {isPublicAccess && (
            <Typography className={classes.disabledHint}>
              Change Learners’ authentication from Public to enable payments.
            </Typography>
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        {!isPaidCompatibleHeader && (
        <div className={classes.infoRow}>
          <Icon
            icon="material-symbols:info-rounded"
            style={{
              width: '20px',
              height: '20px',
              color: 'var(--mcg-gray, #919EAB)',
              flexShrink: 0,
            }}
          />
          <Typography className={classes.infoText}>
            For paid certificates to work, the collection’s landing page header
            must be set to Hero or Promo.{' '}
            <Link
              component="button"
              type="button"
              className={classes.infoLink}
              onClick={() =>
                history.push(
                  `/collection/${collection.id}/landing-page/editor`,
                )
              }
            >
              Change
            </Link>
          </Typography>
        </div>
        )}
        {loaded && (
          <PaidAccess
            collectionId={collection.id}
            entity={collection}
            paymentSettingType="certificate"
            settingsLoaded={loaded}
            hideBanner
            onClickDone={() => {}}
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default withStyles(styles)(PaymentSetup)
